/**
 * LeaderboardTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function LeaderboardTab() {
  const [collection, setCollection] = useState([]);
  const [battle, setBattle] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [totalTrainers, setTotalTrainers] = useState(9);

  useEffect(() => {
    api.get("/palgame/leaderboard/collection?limit=20").then(({ data }) => setCollection(data.entries || []));
    api.get("/palgame/leaderboard/battle?limit=20").then(({ data }) => setBattle(data.entries || []));
    api.get("/palgame/leaderboard/trainers?limit=20").then(({ data }) => {
      setTrainers(data.entries || []);
      if (data.total_trainers) setTotalTrainers(data.total_trainers);
    });
  }, []);

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 fade-up">
      <Board title="Top Collectors" rows={collection} field="pal_count" suffix=" pals" />
      <Board title="Top Battlers" rows={battle} field="wins" suffix=" wins" />
      <TrainerBoard rows={trainers} totalTrainers={totalTrainers} />
    </div>
  );
}

function Board({ title, rows, field, suffix }) {
  const { isMaster } = useMasterTrainers();
  return (
    <div className="glass-card p-6">
      <h3 className="label mb-3">{title}</h3>
      <ul className="divide-y divide-white/5">
        {rows.map((r) => {
          const master = r.is_master || isMaster(r.user_name);
          return (
            <li key={r.user_id} className="flex items-center justify-between py-2.5">
              <span className={`w-6 ${r.rank === 1 ? "glow-gold" : r.rank === 2 ? "text-[#c0c0c0]" : r.rank === 3 ? "text-[#cd7f32]" : "text-[var(--muted)]"}`}>{r.rank}</span>
              <span className="flex-1 truncate px-2 flex items-center gap-1">
                <span className="truncate">{r.user_name || r.user_id}</span>
                {master && (
                  <span className="text-[var(--gold)] text-[10px] leading-none shrink-0" title="Master Trainer">★</span>
                )}
              </span>
              <span className="glow-teal">{r[field] ?? 0}{suffix}</span>
            </li>
          );
        })}
        {rows.length === 0 && <li className="py-6 text-center text-[var(--muted)] text-sm">No data.</li>}
      </ul>
    </div>
  );
}

function TrainerBoard({ rows, totalTrainers }) {
  return (
    <div className="glass-card p-6" data-testid="leaderboard-trainers">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="label">Top Trainers</h3>
        <span className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
          of {totalTrainers}
        </span>
      </div>
      <ul className="divide-y divide-white/5">
        {rows.map((r) => (
          <li key={r.user_id} className="flex items-center justify-between py-2.5">
            <span className={`w-6 ${r.rank === 1 ? "glow-gold" : r.rank === 2 ? "text-[#c0c0c0]" : r.rank === 3 ? "text-[#cd7f32]" : "text-[var(--muted)]"}`}>{r.rank}</span>
            <span className="flex-1 truncate px-2 flex items-center gap-1">
              <span className="truncate">{r.user_name || r.user_id}</span>
              {r.is_master && (
                <span
                  className="text-[var(--gold)] text-[10px] leading-none shrink-0"
                  title="Master Trainer — defeated all 9 NPC trainers"
                  data-testid="leaderboard-master-star"
                >
                  ★
                </span>
              )}
            </span>
            <span className="text-right">
              <span className="glow-teal">{r.distinct_cnt}</span>
              <span className="text-[var(--muted)] text-xs">/{totalTrainers}</span>
              {r.total_defeats > r.distinct_cnt && (
                <span className="text-[10px] text-[var(--muted)] ml-1">
                  ({r.total_defeats} wins)
                </span>
              )}
            </span>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="py-6 text-center text-[var(--muted)] text-sm">
            No trainer wins yet — be the first.
          </li>
        )}
      </ul>
    </div>
  );
}



// ============================================================================
// ARENA TAB — 3-pal team battles vs NPC trainers
// ============================================================================

